<template>
  <div class="gl-form-page">
    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-form-header">
        <div>
          <div class="gl-form-breadcrumb">
            <router-link to="/">Home</router-link>
            <span>/</span>
            <router-link to="/owner/branches">Branches</router-link>
            <span>/</span>
            <span>{{ isEdit ? 'Edit' : 'Create' }}</span>
          </div>
          <h1 class="gl-form-title">{{ isEdit ? 'Edit Branch' : 'Create Branch' }}</h1>
        </div>
      </div>
    </div>

    <div class="container-fluid">
      <!-- Loading -->
      <div v-if="loading" class="gl-loading">
        <div class="spinner-border" role="status" style="color:#900C27;width:28px;height:28px;border-width:3px"></div>
        <span>Loading branch…</span>
      </div>

      <form v-else @submit.prevent="submitForm" class="gl-form-body">

        <!-- Live preview strip -->
        <div class="gl-preview-strip" v-if="form.name">
          <div class="gl-preview-avatar">{{ form.name.charAt(0).toUpperCase() }}{{ (form.name.split(' ')[1] || '').charAt(0).toUpperCase() }}</div>
          <div class="gl-preview-info">
            <div class="gl-preview-name">{{ form.name }}</div>
            <div class="gl-preview-meta">
              <span v-if="form.slug" class="gl-preview-tag">{{ form.slug }}</span>
              <span v-if="form.city" class="gl-preview-tag">{{ form.city }}</span>
              <span v-if="form.state" class="gl-preview-tag">{{ form.state }}</span>
            </div>
          </div>
          <div :class="['gl-preview-status', form.is_active ? 'active' : 'inactive']">
            <span class="gl-preview-dot"></span>{{ form.is_active ? 'Active' : 'Inactive' }}
          </div>
        </div>

        <!-- ── Section 1: Basic Information ── -->
        <div class="gl-section" style="animation-delay:0ms">
          <div class="gl-section-header">
            <div class="gl-section-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </div>
            <div>
              <div class="gl-section-title">Basic Information</div>
              <div class="gl-section-desc">Name, identifier, and contact details</div>
            </div>
          </div>

          <div class="gl-fields">
            <div class="gl-field-row">
              <div class="gl-field">
                <label class="gl-label">Branch Name <span class="gl-req">*</span></label>
                <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.name }" v-model="form.name" placeholder="e.g. USJ 11" required>
                <div v-if="errors.name" class="gl-err">{{ errors.name[0] }}</div>
              </div>
              <div class="gl-field">
                <label class="gl-label">Slug <span class="gl-req">*</span></label>
                <div class="gl-input-prefix-wrap">
                  <span class="gl-input-prefix">@</span>
                  <input type="text" class="gl-input gl-input-has-prefix" :class="{ 'gl-input-err': errors.slug }" v-model="form.slug" placeholder="usj-11" required>
                </div>
                <div class="gl-hint">URL-friendly identifier, auto-generated from name</div>
                <div v-if="errors.slug" class="gl-err">{{ errors.slug[0] }}</div>
              </div>
            </div>

            <div class="gl-field-row">
              <div class="gl-field">
                <label class="gl-label">Phone</label>
                <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.phone }" v-model="form.phone" placeholder="+60193934247">
                <div v-if="errors.phone" class="gl-err">{{ errors.phone[0] }}</div>
              </div>
              <div class="gl-field">
                <label class="gl-label">Email</label>
                <input type="email" class="gl-input" :class="{ 'gl-input-err': errors.email }" v-model="form.email" placeholder="branch@example.com">
                <div v-if="errors.email" class="gl-err">{{ errors.email[0] }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Section 2: Address ── -->
        <div class="gl-section" style="animation-delay:60ms">
          <div class="gl-section-header">
            <div class="gl-section-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div>
              <div class="gl-section-title">Address</div>
              <div class="gl-section-desc">Physical location of this branch</div>
            </div>
          </div>

          <div class="gl-fields">
            <div class="gl-field">
              <label class="gl-label">Street Address <span class="gl-req">*</span></label>
              <textarea class="gl-input gl-textarea" :class="{ 'gl-input-err': errors.address }" v-model="form.address" rows="2" placeholder="No. 12, Jalan USJ 11/2A…" required></textarea>
              <div v-if="errors.address" class="gl-err">{{ errors.address[0] }}</div>
            </div>

            <div class="gl-field-row gl-field-row-3">
              <div class="gl-field">
                <label class="gl-label">City <span class="gl-req">*</span></label>
                <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.city }" v-model="form.city" placeholder="Subang Jaya" required>
                <div v-if="errors.city" class="gl-err">{{ errors.city[0] }}</div>
              </div>
              <div class="gl-field">
                <label class="gl-label">Postcode <span class="gl-req">*</span></label>
                <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.postcode }" v-model="form.postcode" placeholder="47620" required>
                <div v-if="errors.postcode" class="gl-err">{{ errors.postcode[0] }}</div>
              </div>
              <div class="gl-field">
                <label class="gl-label">State <span class="gl-req">*</span></label>
                <select class="gl-input gl-select" :class="{ 'gl-input-err': errors.state }" v-model="form.state" required>
                  <option value="">Select State</option>
                  <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
                </select>
                <div v-if="errors.state" class="gl-err">{{ errors.state[0] }}</div>
              </div>
            </div>

            <div class="gl-field-row">
              <div class="gl-field">
                <label class="gl-label">Latitude <span class="gl-optional">(optional)</span></label>
                <input type="number" step="any" class="gl-input" :class="{ 'gl-input-err': errors.latitude }" v-model="form.latitude" placeholder="3.0738">
                <div v-if="errors.latitude" class="gl-err">{{ errors.latitude[0] }}</div>
              </div>
              <div class="gl-field">
                <label class="gl-label">Longitude <span class="gl-optional">(optional)</span></label>
                <input type="number" step="any" class="gl-input" :class="{ 'gl-input-err': errors.longitude }" v-model="form.longitude" placeholder="101.5830">
                <div v-if="errors.longitude" class="gl-err">{{ errors.longitude[0] }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Section 3: Order Types ── -->
        <div class="gl-section" style="animation-delay:120ms">
          <div class="gl-section-header">
            <div class="gl-section-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
            </div>
            <div>
              <div class="gl-section-title">Order Types</div>
              <div class="gl-section-desc">Choose what order modes this branch accepts</div>
            </div>
          </div>

          <div class="gl-order-cards">
            <button
              type="button"
              :class="['gl-order-card', { active: form.accepts_dine_in }]"
              @click="form.accepts_dine_in = !form.accepts_dine_in"
            >
              <div class="gl-oc-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/></svg>
              </div>
              <div class="gl-oc-label">Dine In</div>
              <div class="gl-oc-check">
                <svg v-if="form.accepts_dine_in" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            </button>

            <button
              type="button"
              :class="['gl-order-card', { active: form.accepts_takeaway }]"
              @click="form.accepts_takeaway = !form.accepts_takeaway"
            >
              <div class="gl-oc-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              </div>
              <div class="gl-oc-label">Takeaway</div>
              <div class="gl-oc-check">
                <svg v-if="form.accepts_takeaway" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            </button>

            <button
              type="button"
              :class="['gl-order-card', { active: form.accepts_delivery }]"
              @click="form.accepts_delivery = !form.accepts_delivery"
            >
              <div class="gl-oc-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
              </div>
              <div class="gl-oc-label">Delivery</div>
              <div class="gl-oc-check">
                <svg v-if="form.accepts_delivery" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            </button>
          </div>
        </div>

        <!-- ── Section 4: Status ── -->
        <div class="gl-section gl-section-inline" style="animation-delay:180ms">
          <div class="gl-section-header" style="margin-bottom:0">
            <div class="gl-section-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div style="flex:1">
              <div class="gl-section-title">Branch Status</div>
              <div class="gl-section-desc">{{ form.is_active ? 'Branch is live and accepting orders' : 'Branch is hidden from customers' }}</div>
            </div>
            <button type="button" :class="['gl-toggle', { on: form.is_active }]" @click="form.is_active = !form.is_active" :aria-label="form.is_active ? 'Deactivate' : 'Activate'">
              <span class="gl-toggle-knob"></span>
            </button>
          </div>
        </div>

        <!-- ── Actions ── -->
        <div class="gl-form-actions" style="animation-delay:220ms">
          <router-link to="/owner/branches" class="gl-btn-cancel">Cancel</router-link>
          <button type="submit" class="gl-btn-submit" :disabled="submitting">
            <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ isEdit ? 'Save Changes' : 'Create Branch' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Upgrade Modal -->
    <UpgradeModal
      ref="upgradeModal"
      modal-id="branchUpgradeModal"
      title="Branch Limit Reached"
      :message="upgradeMessage"
      :current-plan="currentPlan"
      resource-label="Branches"
      :used="usageUsed"
      :limit="usageLimit"
    />
  </div>
</template>

<script>
import { ownerBranchApi } from '@/services/ownerApi';
import { subscriptionApi } from '@/services/subscriptionApi';
import UpgradeModal from '@/components/owner/common/UpgradeModal.vue';

export default {
  name: 'BranchForm',
  components: { UpgradeModal },

  data() {
    return {
      loading: false,
      submitting: false,
      isEdit: false,
      errors: {},
      upgradeMessage: '',
      currentPlan: '',
      usageUsed: 0,
      usageLimit: 0,
      form: {
        name: '',
        slug: '',
        address: '',
        city: '',
        postcode: '',
        state: '',
        phone: '',
        email: '',
        latitude: null,
        longitude: null,
        is_active: true,
        accepts_dine_in: true,
        accepts_takeaway: true,
        accepts_delivery: false
      },
      states: [
        'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan',
        'Pahang', 'Perak', 'Perlis', 'Pulau Pinang', 'Sabah',
        'Sarawak', 'Selangor', 'Terengganu', 'Kuala Lumpur', 'Labuan', 'Putrajaya'
      ]
    };
  },

  watch: {
    'form.name'(val) {
      if (!this.isEdit) {
        this.form.slug = this.slugify(val);
      }
    }
  },

  mounted() {
    if (this.$route.params.id) {
      this.isEdit = true;
      this.fetchBranch();
    }
  },

  methods: {
    slugify(text) {
      return text.toString().toLowerCase().trim()
        .replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-');
    },

    async fetchBranch() {
      try {
        this.loading = true;
        const response = await ownerBranchApi.get(this.$route.params.id);
        const branch = response.data.data;
        this.form = {
          name: branch.name, slug: branch.slug,
          address: branch.address, city: branch.city,
          postcode: branch.postcode, state: branch.state,
          phone: branch.phone || '', email: branch.email || '',
          latitude: branch.latitude, longitude: branch.longitude,
          is_active: branch.is_active,
          accepts_dine_in: branch.accepts_dine_in,
          accepts_takeaway: branch.accepts_takeaway,
          accepts_delivery: branch.accepts_delivery
        };
      } catch (error) {
        console.error('Failed to fetch branch:', error);
        this.$router.push('/owner/branches');
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      try {
        this.submitting = true;
        this.errors = {};
        if (this.isEdit) {
          await ownerBranchApi.update(this.$route.params.id, this.form);
        } else {
          await ownerBranchApi.create(this.form);
        }
        this.$router.push('/owner/branches');
      } catch (error) {
        console.error('Failed to save branch:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          const message = error.response?.data?.message || 'Failed to save branch';
          if (this.isPlanLimitError(message)) {
            this.showUpgradeModal(message);
          } else {
            alert(message);
          }
        }
      } finally {
        this.submitting = false;
      }
    },

    isPlanLimitError(message) {
      const limitKeywords = ['limit', 'maximum', 'exceeded', 'upgrade', 'plan'];
      return limitKeywords.some(k => message.toLowerCase().includes(k));
    },

    async showUpgradeModal(message) {
      this.upgradeMessage = message;
      try {
        const response = await subscriptionApi.getUsage();
        const data = response.data.data;
        this.currentPlan = data.plan?.name || 'Free';
        this.usageUsed = data.limits?.outlets?.used || 0;
        this.usageLimit = data.limits?.outlets?.limit || 0;
      } catch (e) {
        console.error('Failed to fetch usage:', e);
      }
      this.$refs.upgradeModal?.show();
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-form-page { padding-bottom: 48px; }

.gl-form-header {
  display: flex;
  align-items: flex-end;
  padding: 28px 0 20px;
}
.gl-form-breadcrumb {
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; color: #9ca3af; margin-bottom: 4px;
}
.gl-form-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-form-breadcrumb a:hover { color: #900C27; }
.gl-form-title {
  font-size: 22px; font-weight: 700; color: #111318;
  letter-spacing: -0.4px; margin: 0;
}

.gl-loading {
  display: flex; align-items: center; gap: 12px;
  padding: 60px 0; justify-content: center; font-size: 14px; color: #9ca3af;
}

/* ── Form body ── */
.gl-form-body { max-width: 760px; display: flex; flex-direction: column; gap: 12px; }

/* ── Live preview strip ── */
.gl-preview-strip {
  display: flex; align-items: center; gap: 12px;
  background: #fff; border: 1.5px solid rgba(144,12,39,0.2);
  border-radius: 14px; padding: 12px 16px;
  animation: gl-fade-in 0.25s ease;
}
@keyframes gl-fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-preview-avatar {
  width: 38px; height: 38px; border-radius: 10px;
  background: linear-gradient(135deg, #900C27, #6f0a1e);
  color: #fff; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; letter-spacing: 0.3px;
}
.gl-preview-info { flex: 1; min-width: 0; }
.gl-preview-name { font-size: 14px; font-weight: 700; color: #111318; }
.gl-preview-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 3px; }
.gl-preview-tag {
  font-size: 11px; background: #f3f4f6; color: #6b7280;
  padding: 2px 8px; border-radius: 20px; font-weight: 500;
}
.gl-preview-status {
  display: flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 600; padding: 4px 10px;
  border-radius: 20px; flex-shrink: 0;
}
.gl-preview-status.active { background: rgba(144,12,39,0.10); color: #900C27; }
.gl-preview-status.inactive { background: #f3f4f6; color: #9ca3af; }
.gl-preview-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: currentColor; flex-shrink: 0;
}
.gl-preview-status.active .gl-preview-dot {
  animation: gl-pulse 2s ease infinite;
}
@keyframes gl-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(144,12,39,0.4); }
  70%  { box-shadow: 0 0 0 5px rgba(144,12,39,0); }
  100% { box-shadow: 0 0 0 0 rgba(144,12,39,0); }
}

/* ── Section ── */
.gl-section {
  background: #fff;
  border: 1.5px solid #f0f1f3;
  border-radius: 16px;
  padding: 22px;
  animation: gl-sect-in 0.3s ease both;
}
@keyframes gl-sect-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-section-inline { padding: 18px 22px; }
.gl-section-header {
  display: flex; align-items: center; gap: 12px;
  margin-bottom: 20px;
}
.gl-section-icon {
  width: 34px; height: 34px; border-radius: 9px;
  background: rgba(144,12,39,0.10); color: #900C27;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.gl-section-title { font-size: 14px; font-weight: 700; color: #111318; }
.gl-section-desc { font-size: 12px; color: #9ca3af; margin-top: 1px; }

/* ── Fields ── */
.gl-fields { display: flex; flex-direction: column; gap: 16px; }
.gl-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.gl-field-row-3 { grid-template-columns: 1fr 1fr 1fr; }
.gl-field { display: flex; flex-direction: column; gap: 5px; }

.gl-label {
  font-size: 12.5px; font-weight: 600; color: #374151;
}
.gl-req { color: #ef4444; margin-left: 1px; }
.gl-optional { color: #9ca3af; font-weight: 400; margin-left: 2px; }
.gl-hint { font-size: 11.5px; color: #9ca3af; }
.gl-err { font-size: 11.5px; color: #ef4444; margin-top: 2px; }

.gl-input {
  height: 40px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 13.5px;
  color: #111318;
  background: #fafafa;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  width: 100%;
}
.gl-input:focus {
  border-color: #900C27;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(144,12,39,0.10);
}
.gl-input::placeholder { color: #c4c8d0; }
.gl-input-err { border-color: #ef4444 !important; }
.gl-input-err:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.10) !important; }

.gl-textarea { height: auto; padding: 10px 12px; resize: vertical; }
.gl-select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 36px; cursor: pointer; }

/* Slug prefix input */
.gl-input-prefix-wrap { position: relative; }
.gl-input-prefix {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  font-size: 13.5px; color: #9ca3af; font-weight: 500; pointer-events: none;
}
.gl-input-has-prefix { padding-left: 24px; }

/* ── Order type cards ── */
.gl-order-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.gl-order-card {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 18px 12px; border-radius: 12px;
  border: 1.5px solid #e5e7eb; background: #fafafa;
  cursor: pointer; transition: all 0.15s; position: relative;
  text-align: center;
}
.gl-order-card:hover { border-color: #900C27; background: rgba(144,12,39,0.04); }
.gl-order-card.active {
  border-color: #900C27; background: rgba(144,12,39,0.07);
}
.gl-oc-icon { color: #9ca3af; transition: color 0.15s; }
.gl-order-card.active .gl-oc-icon { color: #900C27; }
.gl-order-card:hover .gl-oc-icon { color: #900C27; }
.gl-oc-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-order-card.active .gl-oc-label { color: #900C27; }
.gl-oc-check {
  position: absolute; top: 8px; right: 8px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #900C27; display: flex; align-items: center; justify-content: center;
  color: #fff; opacity: 0; transition: opacity 0.15s;
}
.gl-order-card.active .gl-oc-check { opacity: 1; }

/* ── Status toggle ── */
.gl-toggle {
  width: 44px; height: 24px; border-radius: 12px;
  border: none; cursor: pointer; padding: 0;
  background: #e5e7eb; position: relative;
  transition: background 0.2s; flex-shrink: 0;
}
.gl-toggle.on { background: #900C27; }
.gl-toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fff; transition: transform 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
  display: block;
}
.gl-toggle.on .gl-toggle-knob { transform: translateX(20px); }

/* ── Actions ── */
.gl-form-actions {
  display: flex; align-items: center; gap: 10px;
  animation: gl-sect-in 0.3s ease both;
}
.gl-btn-cancel {
  padding: 10px 20px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600;
  text-decoration: none; transition: all 0.15s;
}
.gl-btn-cancel:hover { border-color: #d1d5db; background: #f9fafb; color: #374151; }
.gl-btn-submit {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 10px; border: none;
  background: #900C27; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-btn-submit:hover:not(:disabled) {
  background: #6f0a1e; transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(144,12,39,0.30);
}
.gl-btn-submit:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  /* Give room for sticky action bar */
  .gl-form-page { padding-bottom: 84px; }

  /* Compact header */
  .gl-form-header { padding: 14px 0 12px; }
  .gl-form-title { font-size: 19px !important; letter-spacing: -0.3px; }
  .gl-form-breadcrumb { font-size: 11px; }

  /* Full-width form body */
  .gl-form-body { max-width: 100%; gap: 10px; }

  /* Stack all field rows to single column */
  .gl-field-row,
  .gl-field-row-3 { grid-template-columns: 1fr !important; gap: 12px; }

  /* Compact sections */
  .gl-section { padding: 15px 16px; border-radius: 14px; }
  .gl-section-inline { padding: 13px 16px; }
  .gl-section-header { margin-bottom: 16px; gap: 10px; }
  .gl-section-icon { width: 30px; height: 30px; border-radius: 8px; }

  /* Larger touch targets for inputs */
  .gl-input { height: 46px; font-size: 15px; border-radius: 10px; }
  .gl-textarea { height: auto; }

  /* Order type cards — horizontal rows on mobile */
  .gl-order-cards { grid-template-columns: 1fr; gap: 8px; }
  .gl-order-card {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 13px 16px;
    gap: 12px;
    border-radius: 10px;
    text-align: left;
  }
  .gl-oc-icon { flex-shrink: 0; }
  .gl-oc-label { font-size: 13.5px; font-weight: 600; flex: 1; }
  .gl-oc-check { position: static; margin-left: auto; }

  /* Compact preview strip */
  .gl-preview-strip { padding: 10px 13px; gap: 10px; border-radius: 12px; }
  .gl-preview-name { font-size: 13px; }

  /* Sticky action bar at bottom */
  .gl-form-actions {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    background: #fff;
    border-top: 1px solid #eaedf2;
    padding: 10px 16px;
    z-index: 100;
    animation: none;
    box-shadow: 0 -4px 16px rgba(0,0,0,0.06);
  }
  .gl-btn-cancel {
    flex: 0 0 auto;
    padding: 11px 18px;
    font-size: 13px;
  }
  .gl-btn-submit {
    flex: 1;
    justify-content: center;
    padding: 11px 18px;
    font-size: 13px;
  }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-form-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-form-breadcrumb { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-form-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-form-breadcrumb a:hover { color: #900C27 !important; }

body.dark-only .gl-preview-strip { background: #262932 !important; border-color: rgba(144,12,39,0.25) !important; }
body.dark-only .gl-preview-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-preview-tag { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-preview-status.inactive { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.35) !important; }

body.dark-only .gl-section { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-section-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-section-desc { color: rgba(255,255,255,0.35) !important; }

body.dark-only .gl-label { color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-hint { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-optional { color: rgba(255,255,255,0.25) !important; }

body.dark-only .gl-input {
  background: rgba(255,255,255,0.05) !important;
  border-color: #3d4255 !important;
  color: rgba(255,255,255,0.87) !important;
}
body.dark-only .gl-input:focus {
  background: rgba(255,255,255,0.07) !important;
  border-color: #900C27 !important;
  box-shadow: 0 0 0 3px rgba(144,12,39,0.15) !important;
}
body.dark-only .gl-input::placeholder { color: rgba(255,255,255,0.2) !important; }
body.dark-only .gl-input-prefix { color: rgba(255,255,255,0.3) !important; }

body.dark-only .gl-order-card { background: rgba(255,255,255,0.03) !important; border-color: rgba(255,255,255,0.08) !important; }
body.dark-only .gl-order-card:hover { background: rgba(144,12,39,0.12) !important; border-color: rgba(144,12,39,0.5) !important; }
body.dark-only .gl-order-card.active { background: rgba(144,12,39,0.15) !important; border-color: #900C27 !important; box-shadow: 0 0 0 1px rgba(144,12,39,0.3) !important; }
body.dark-only .gl-oc-icon { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-order-card.active .gl-oc-icon { color: #4caf97 !important; }
body.dark-only .gl-oc-label { color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-order-card.active .gl-oc-label { color: #4caf97 !important; }

body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }

body.dark-only .gl-btn-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-btn-cancel:hover { background: #3d4255 !important; color: rgba(255,255,255,0.75) !important; }

@media (max-width: 767px) {
  body.dark-only .gl-form-actions {
    background: #1e2130 !important;
    border-top-color: #323644 !important;
    box-shadow: 0 -4px 16px rgba(0,0,0,0.25) !important;
  }
}
</style>
